import { create } from "zustand";
import type { User } from "@erc/shared";
import { saveSession, getSession, clearSession } from "../lib/auth";
import { apiClient, refreshApiToken } from "../lib/api";
import { sendTokenToExtension } from "../lib/extension";

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  initializeFromStorage: () => void;
}

// Hydrate synchronously at module load so a page refresh keeps the user
// authenticated on the FIRST render (otherwise AppLayout bounces to /login,
// which then redirects to /dashboard, losing the current route).
const initialSession = getSession();
if (initialSession) {
  apiClient.setToken(initialSession.token);
}

export const useAuthStore = create<AuthState>((set) => ({
  user: initialSession?.user ?? null,
  token: initialSession?.token ?? null,
  isLoading: false,
  isAuthenticated: !!initialSession,

  initializeFromStorage: () => {
    const session = getSession();
    if (session) {
      set({
        user: session.user,
        token: session.token,
        isAuthenticated: true,
      });
      apiClient.setToken(session.token);
    }
  },

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const session = await apiClient.login({ email, password });
      saveSession(session);
      apiClient.setToken(session.token);
      refreshApiToken();
      sendTokenToExtension(session.token).catch(() => {});
      set({
        user: session.user,
        token: session.token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  logout: () => {
    clearSession();
    apiClient.setToken(null);
    // Aktif magaza secimi kullaniciya ozeldir: baska hesapla girildiginde
    // bayat X-Shop-Id gonderilmesin.
    localStorage.removeItem("erc_active_shop");
    apiClient.setShopId(null);
    set({
      user: null,
      token: null,
      isAuthenticated: false,
    });
  },
}));
